import { useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, Paperclip } from "lucide-react";
import Seo from "@/components/Seo";
import { ToolShell, TextInput, SelectInput, WhatsAppCTA } from "@/components/energy/tool";
import { api, formatApiError } from "@/lib/solarApi";

const QUESTIONS = [
  { key: "all_off", q: "Is the entire system off?", yes: "Check the main breakers and battery isolator first — a tripped breaker is the most common cause. If breakers trip again immediately, stop and request support below." },
  { key: "inverter_error", q: "Is the inverter displaying an error code or warning light?", yes: "Note the exact code and switch the inverter off, wait 2 minutes, then restart once. If the code returns, do not keep retrying — request support below with the code." },
  { key: "battery_charging", q: "Is the battery charging (indicator or display shows charging)?", no: "If batteries are not charging, check that panels are clean and the solar breaker is on. No charging in full sun usually means a controller or connection issue — request support." },
  { key: "solar_producing", q: "Is solar production showing during daylight?", no: "If the display shows zero production in bright sun, panels may be shaded, dirty or disconnected. Do not open any enclosures — request support." },
  { key: "appliances_powered", q: "Are appliances receiving power?", no: "If the inverter runs but appliances are off, check the AC output breaker and your distribution board. If unsure, request support." },
  { key: "after_outage", q: "Did the problem start after a grid outage?", yes: "Post-outage faults are often surge-related. Keep the system off until checked if anything looks damaged." },
];

export default function EnergyEmergency() {
  const [answers, setAnswers] = useState({});
  const [danger, setDanger] = useState(null);
  const [form, setForm] = useState({ name: "", phone: "", location: "", system_type: "", description: "" });
  const [photo, setPhoto] = useState(null);
  const [video, setVideo] = useState(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const answer = (k, v) => setAnswers((a) => ({ ...a, [k]: v }));
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      if (photo) fd.append("photo", photo);
      if (video) fd.append("video", video);
      await api.post("/energy/support", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setDone(true);
      toast.success("Support request sent");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Seo title="Solar Emergency Assistant — My Solar System Isn't Working | BNM" description="Guided safe troubleshooting for solar systems, plus direct technical support from BNM with photo and video upload." />
      <ToolShell
        testid="emergency-tool"
        eyebrow="BNM Solar Emergency Assistant"
        title="My solar system isn't working."
        lead="Answer a few questions for safe guidance. Never open electrical enclosures or attempt repairs yourself."
      >
        <div className="rounded-lg border-2 border-red-300 bg-red-50 p-5" data-testid="emergency-danger-check">
          <div className="flex gap-3">
            <AlertTriangle className="h-5 w-5 shrink-0 text-red-600 mt-0.5" aria-hidden="true" />
            <div>
              <p className="font-semibold text-red-800 text-sm">First: is there any unusual smell, smoke, heat or noise?</p>
              <div className="mt-3 flex gap-3">
                <button onClick={() => setDanger(true)} data-testid="danger-yes" className="rounded-md bg-red-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-red-700">Yes</button>
                <button onClick={() => setDanger(false)} data-testid="danger-no" className="rounded-md border border-slate-300 bg-white px-5 py-2.5 text-sm font-semibold text-slate-700 hover:border-emerald-400">No</button>
              </div>
            </div>
          </div>
          {danger === true && (
            <p className="mt-4 rounded-md bg-red-600 px-4 py-3 text-sm font-semibold text-white" data-testid="danger-warning">
              Switch off the system if it is safe to do so and contact a qualified technician immediately. Do not touch the equipment. Call BNM: +232 90712518.
            </p>
          )}
        </div>

        {danger === false && (
          <div className="mt-8 rounded-lg border border-slate-200 bg-white p-6 sm:p-8" data-testid="emergency-questions">
            <h2 className="font-display text-lg font-bold text-slate-900">Guided checks</h2>
            <div className="mt-5 space-y-5">
              {QUESTIONS.map((q) => (
                <div key={q.key} className="border-b border-slate-100 pb-5 last:border-0 last:pb-0">
                  <p className="text-sm font-medium text-slate-800">{q.q}</p>
                  <div className="mt-2 flex gap-3">
                    <button onClick={() => answer(q.key, true)} data-testid={`q-${q.key}-yes`}
                      className={`rounded-md border px-4 py-2 text-sm font-medium transition-colors ${answers[q.key] === true ? "border-obsidian bg-obsidian text-white" : "border-slate-300 text-slate-600 hover:border-emerald-400"}`}>Yes</button>
                    <button onClick={() => answer(q.key, false)} data-testid={`q-${q.key}-no`}
                      className={`rounded-md border px-4 py-2 text-sm font-medium transition-colors ${answers[q.key] === false ? "border-obsidian bg-obsidian text-white" : "border-slate-300 text-slate-600 hover:border-emerald-400"}`}>No</button>
                  </div>
                  {answers[q.key] === true && q.yes && <p className="mt-3 rounded-md bg-mist p-3.5 text-sm text-slate-600">{q.yes}</p>}
                  {answers[q.key] === false && q.no && <p className="mt-3 rounded-md bg-mist p-3.5 text-sm text-slate-600">{q.no}</p>}
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="mt-10">
          <h2 className="font-display text-xl font-bold text-slate-900">Request BNM Technical Support</h2>
          {done ? (
            <div className="mt-5 rounded-lg border border-emerald-300 bg-emerald-50 p-6 text-center" data-testid="support-success">
              <p className="font-display text-lg font-bold text-slate-900">Support request received</p>
              <p className="mt-2 text-sm text-slate-600">BNM will contact you on the number you provided.</p>
            </div>
          ) : (
            <form onSubmit={submit} data-testid="support-form" className="mt-5 rounded-lg border border-slate-200 bg-white p-6 sm:p-8">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <TextInput label="Name" required testid="support-name" value={form.name} onChange={set("name")} />
                <TextInput label="Phone / WhatsApp" required testid="support-phone" value={form.phone} onChange={set("phone")} type="tel" />
                <TextInput label="Location" testid="support-location" value={form.location} onChange={set("location")} />
                <SelectInput label="System type" testid="support-system" value={form.system_type} onChange={set("system_type")}
                  options={[{ value: "", label: "Select…" }, { value: "solar_battery", label: "Solar + battery" }, { value: "solar_grid_battery", label: "Solar + grid + battery" }, { value: "inverter_only", label: "Inverter/backup only" }, { value: "generator", label: "Generator" }, { value: "other", label: "Other / not sure" }]} />
                <label className="block sm:col-span-2">
                  <span className="block text-sm font-medium text-slate-700">Describe the problem *</span>
                  <textarea rows={4} required value={form.description} data-testid="support-description" onChange={(e) => set("description")(e.target.value)}
                    className="mt-1.5 w-full rounded-md border border-slate-300 bg-white px-3.5 py-2.5 text-sm text-slate-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-600" />
                </label>
                <label className="block">
                  <span className="block text-sm font-medium text-slate-700">Photo (optional, max 8 MB)</span>
                  <span className="mt-1.5 flex items-center gap-2 rounded-md border border-slate-300 bg-white px-3.5 py-3 text-sm text-slate-500 cursor-pointer hover:border-emerald-400">
                    <Paperclip className="h-4 w-4" aria-hidden="true" />
                    {photo ? photo.name : "Choose photo"}
                    <input type="file" accept="image/*" className="hidden" data-testid="support-photo" onChange={(e) => setPhoto(e.target.files[0] || null)} />
                  </span>
                </label>
                <label className="block">
                  <span className="block text-sm font-medium text-slate-700">Video (optional, max 8 MB)</span>
                  <span className="mt-1.5 flex items-center gap-2 rounded-md border border-slate-300 bg-white px-3.5 py-3 text-sm text-slate-500 cursor-pointer hover:border-emerald-400">
                    <Paperclip className="h-4 w-4" aria-hidden="true" />
                    {video ? video.name : "Choose video"}
                    <input type="file" accept="video/*" className="hidden" data-testid="support-video" onChange={(e) => setVideo(e.target.files[0] || null)} />
                  </span>
                </label>
              </div>
              {error && <p role="alert" className="mt-4 text-sm text-red-600">{error}</p>}
              <div className="mt-6 flex flex-wrap gap-3">
                <button type="submit" disabled={busy} data-testid="support-submit"
                  className="rounded-md bg-obsidian px-7 py-3.5 font-semibold text-white hover:bg-pine hover:text-volt transition-colors disabled:opacity-60">
                  {busy ? "Sending…" : "Request BNM Technical Support"}
                </button>
                <WhatsAppCTA message="Hello BNM. My solar system isn't working and I need technical support." label="WhatsApp BNM Support" />
              </div>
            </form>
          )}
        </div>
      </ToolShell>
    </>
  );
}
